import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { api, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Combobox } from "@/components/Combobox";
import { SortHeader, useOwnerSort } from "@/components/SortHeader";
import { DatePicker } from "@/components/DatePicker";
import { usePaged, PageSize, Pager } from "@/components/Toolbar";
import { fmtNumber, fmtCurrency } from "@/lib/format";
import { rowTone, mixIndexOf, MixChip, OwnerBadge, filterByLoadLocation } from "@/components/RowTone";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, RotateCw, FileSpreadsheet, Printer, Search, CalendarIcon, X, Layers, ScanLine, Fuel, Upload, Link2, Link2Off, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { DieselDiffDialog } from "@/components/DieselDiffDialog";

const VEHICLE_TYPES = ["Open", "Container", "Trailer", "Tipper", "Bulker"];
const dt = (s) => (s ? new Date(s).toLocaleDateString("en-GB") : "-");
const toISO = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");
const ymd = (d) => toISO(d);

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><label className="text-xs font-medium text-muted-foreground">{label}</label>{children}</div>
);
const IconBtn = ({ children, ...p }) => (
  <button className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-accent transition-colors" {...p}>{children}</button>
);
const Section = ({ title, hint, children }) => (
  <div className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-4">
    <div className="flex items-center justify-between">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-mono">{title}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
    {children}
  </div>
);

export default function Dispatch() {
  const { selectedHub, hubs } = useApp();
  const isDalmia = ["dalmia", "dalmia dedicated"].includes((hubs.find((h) => h.id === selectedHub)?.name || "").trim().toLowerCase());
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [material, setMaterial] = useState("all");
  const [vtype, setVtype] = useState("all");
  const [loc, setLoc] = useState("all");
  const [dloc, setDloc] = useState("all");
  const [open, setOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loadLocs, setLoadLocs] = useState([]);
  const [delLocs, setDelLocs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [mixTarget, setMixTarget] = useState(null);
  const [mixSelected, setMixSelected] = useState([]);
  const [mixOriginalIds, setMixOriginalIds] = useState([]);

  const emptyLine = () => ({ material_type: materials[0] || "Cement", load_location: loadLocs[0] || "", delivery_location: "", delivery_number: "", quantity: "", company_rate: "", market_rate: "", diesel: "", cash: "", bank_transfer: "", fastag: "", fuel_qty: "", unloading_amount: "" });
  const empty = {
    gr_no: "", gr_date: new Date().toISOString().slice(0, 10), material_type: "Cement",
    load_location: "", delivery_location: "", vehicle_number: "", owner_name: "",
    vehicle_type: "Open", delivery_number: "", quantity: "", company_rate: "", market_rate: "",
    diesel: "", cash: "", bank_transfer: "", fastag: "", fuel_qty: "", unloading_amount: "", hub_id: "",
  };
  const [f, setF] = useState(empty);
  const [marketEnabled, setMarketEnabled] = useState(true);
  const showMarket = marketEnabled && !isDalmia;
  const [mixed, setMixed] = useState(false);
  const [lines, setLines] = useState([]);

  const load = useCallback(async () => {
    const params = { hub: selectedHub };
    if (material !== "all") params.material = material;
    if (vtype !== "all") params.vehicle_type = vtype;
    if (q) params.q = q;
    let data = (await api.get("/challans", { params })).data;
    if (from) data = data.filter((d) => d.gr_date >= from);
    if (to) data = data.filter((d) => d.gr_date <= to);
    setRows(data);
    api.get("/vehicles", { params: { hub: selectedHub } }).then((r) => setVehicles(r.data));
  }, [selectedHub, material, vtype, q, from, to]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const p = { params: { hub: selectedHub } };
    api.get("/master/material", p).then((r) => setMaterials(r.data.map((x) => x.name)));
    api.get("/master/load_location", p).then((r) => setLoadLocs(r.data.map((x) => x.name)));
    api.get("/master/delivery_location", p).then((r) => setDelLocs(r.data.map((x) => x.name)));
  }, [selectedHub]);

  // Dalmia: auto-fill Material & Load Location from master data (single form + mixed lines)
  useEffect(() => {
    if (!open || editing || !isDalmia) return;
    const m = materials[0], ll = loadLocs[0];
    if (!m && !ll) return;
    setF((p) => ({ ...p, material_type: p.material_type || m || "", load_location: p.load_location || ll || "" }));
    setLines((prev) => prev.map((l) => ({ ...l, material_type: l.material_type || m || "", load_location: l.load_location || ll || "" })));
  }, [open, editing, isDalmia, materials, loadLocs]);

  const locOptions = useMemo(() => [...new Set(rows.map((r) => (r.load_location || "").trim()).filter(Boolean))].sort(), [rows]);
  const dlocOptions = useMemo(() => [...new Set(rows.map((r) => (r.delivery_location || "").trim()).filter(Boolean))].sort(), [rows]);
  const locRows = useMemo(() => filterByLoadLocation(filterByLoadLocation(rows, loc), dloc, "delivery_location"), [rows, loc, dloc]);
  const { sorted: osRows, dir: osDir, toggle: osToggle } = useOwnerSort(locRows);
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows, start } = usePaged(osRows);
  const mixIdx = mixIndexOf(osRows);
  // Mixed load = one serial number: rows sharing group_id share a serial; number shown on the group's first row.
  const serialByRow = {}; const firstOfUnit = {}; const lastOfUnit = {}; const unitKeyByRow = {}; const unitAgg = {}; const unitCount = {};
  { const seen = {}; let n = 0; osRows.forEach((r) => { const k = r.group_id || r.id; unitKeyByRow[r.id] = k; if (!(k in seen)) { seen[k] = ++n; firstOfUnit[r.id] = true; } serialByRow[r.id] = seen[k]; unitCount[k] = (unitCount[k] || 0) + 1; if (!unitAgg[k]) unitAgg[k] = { diesel: 0, bank_transfer: 0, fuel_qty: 0 }; unitAgg[k].diesel += Number(r.diesel) || 0; unitAgg[k].bank_transfer += Number(r.bank_transfer) || 0; unitAgg[k].fuel_qty += Number(r.fuel_qty) || 0; }); }
  { const last = {}; osRows.forEach((r) => { last[unitKeyByRow[r.id]] = r.id; }); osRows.forEach((r) => { lastOfUnit[r.id] = last[unitKeyByRow[r.id]] === r.id; }); }
  const canonRowByUnit = {}; rows.forEach((r) => { const k = r.group_id || r.id; if (!(k in canonRowByUnit)) canonRowByUnit[k] = r; });
  const vehicleNumbers = vehicles.map((v) => v.vehicle_number);
  const inFleet = (vn) => vehicles.some((v) => (v.vehicle_number || "").trim().toLowerCase() === (vn || "").trim().toLowerCase());

  const openCreate = () => {
    setEditing(null); setMixed(false); setMarketEnabled(!isDalmia);
    setF({ ...empty, material_type: materials[0] || "Cement", load_location: loadLocs[0] || "", hub_id: selectedHub !== "all" ? selectedHub : (hubs[0]?.id || "") });
    setLines([emptyLine(), emptyLine()]);
    setMixOriginalIds([]);
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r.id); setMarketEnabled(r.market_rate_enabled !== false);
    const group = r.group_id ? rows.filter((x) => x.group_id === r.group_id) : [];
    const isMixed = group.length > 1;
    setMixed(isMixed);
    setMixOriginalIds(isMixed ? group.map((x) => x.id) : [r.id]);
    const agg = isMixed
      ? group.reduce((a, x) => ({ diesel: a.diesel + (Number(x.diesel) || 0), bank_transfer: a.bank_transfer + (Number(x.bank_transfer) || 0), fuel_qty: a.fuel_qty + (Number(x.fuel_qty) || 0) }), { diesel: 0, bank_transfer: 0, fuel_qty: 0 })
      : { diesel: r.diesel, bank_transfer: r.bank_transfer, fuel_qty: r.fuel_qty };
    setF({
      ...r, quantity: String(r.quantity), company_rate: String(r.company_rate), market_rate: String(r.market_rate),
      diesel: String(agg.diesel || ""), cash: String(r.cash || ""), bank_transfer: String(agg.bank_transfer || ""), fastag: String(r.fastag || ""), fuel_qty: String(agg.fuel_qty || ""), unloading_amount: String(r.unloading_amount || ""),
    });
    setLines(isMixed
      ? group.map((x) => ({ id: x.id, group_id: x.group_id, material_type: x.material_type || "", load_location: x.load_location || "", delivery_location: x.delivery_location || "", delivery_number: x.delivery_number || "", quantity: String(x.quantity), company_rate: String(x.company_rate), market_rate: String(x.market_rate || 0), unloading_amount: String(x.unloading_amount || "") }))
      : [emptyLine(), emptyLine()]);
    setOpen(true);
  };

  // Editable vehicle: auto-fill owner + type when the number matches a fleet vehicle.
  const onVehicleChange = (vn) => {
    setF((p) => {
      const v = vehicles.find((x) => x.vehicle_number.toLowerCase() === (vn || "").trim().toLowerCase());
      return { ...p, vehicle_number: vn, owner_name: v ? v.owner_name : p.owner_name, vehicle_type: v ? v.vehicle_type : p.vehicle_type };
    });
  };

  const setLine = (i, k, v) => setLines((p) => p.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const [invOcr, setInvOcr] = useState(false);
  const handleInvoiceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setInvOcr(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/challans/invoice-ocr`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") },
      });
      setF((p) => {
        const vn = data.vehicle_number || p.vehicle_number;
        const v = vehicles.find((x) => x.vehicle_number.toLowerCase() === (vn || "").trim().toLowerCase());
        return {
          ...p,
          gr_date: data.invoice_date || p.gr_date,
          delivery_number: data.shipment_number || p.delivery_number,
          vehicle_number: vn,
          owner_name: v ? v.owner_name : p.owner_name,
          vehicle_type: v ? v.vehicle_type : p.vehicle_type,
          delivery_location: data.delivery_name || p.delivery_location,
          material_type: data.material_type || p.material_type,
          load_location: data.load_location || p.load_location,
          quantity: data.quantity ? String(data.quantity) : p.quantity,
        };
      });
      toast.success("Invoice read — review the auto-filled fields");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the invoice");
    } finally {
      setInvOcr(false);
    }
  };

  const [mixInvOcr, setMixInvOcr] = useState(false);
  const handleMixedInvoiceFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setMixInvOcr(true);
    const token = localStorage.getItem("tl_token");
    const cid = localStorage.getItem("tl_company");
    const parsed = [];
    let head = null;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/challans/invoice-ocr`, fd, {
          headers: { Authorization: `Bearer ${token}`, "X-Company-Id": cid },
        });
        if (!head) head = data;
        parsed.push({
          ...emptyLine(),
          delivery_number: data.shipment_number || "",
          delivery_location: data.delivery_name || "",
          material_type: data.material_type || materials[0] || "Cement",
          load_location: data.load_location || loadLocs[0] || "",
          quantity: data.quantity ? String(data.quantity) : "",
        });
      }
      if (parsed.length) {
        setLines(parsed.length >= 2 ? parsed : [...parsed, emptyLine()]);
        if (head) {
          setF((p) => {
            const vn = head.vehicle_number || p.vehicle_number;
            const v = vehicles.find((x) => x.vehicle_number.toLowerCase() === (vn || "").trim().toLowerCase());
            return { ...p, gr_date: head.invoice_date || p.gr_date, vehicle_number: vn, owner_name: v ? v.owner_name : p.owner_name, vehicle_type: v ? v.vehicle_type : p.vehicle_type };
          });
        }
        toast.success(`${parsed.length} invoice(s) read — review the delivery lines`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read one of the invoices");
    } finally {
      setMixInvOcr(false);
    }
  };

  const [fuelOpen, setFuelOpen] = useState(false);
  const [fuelRow, setFuelRow] = useState(null);
  const [fuelData, setFuelData] = useState({ fuel_qty: "", diesel: "" });
  const [fuelOcr, setFuelOcr] = useState(false);
  const openFuel = (r) => { setFuelRow(r); setFuelData({ fuel_qty: String(r.fuel_qty || ""), diesel: String(r.diesel || "") }); setFuelOpen(true); };
  const handleFuelFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFuelOcr(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/challans/fuel-ocr`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") },
      });
      setFuelData((p) => ({
        fuel_qty: data.fuel_qty ? String(data.fuel_qty) : p.fuel_qty,
        diesel: data.amount ? String(data.amount) : p.diesel,
      }));
      toast.success("Fuel receipt read — review the values");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the fuel receipt");
    } finally {
      setFuelOcr(false);
    }
  };
  const saveFuel = async () => {
    if (!fuelRow) return;
    try {
      await api.put(`/challans/${fuelRow.id}`, { ...fuelRow, fuel_qty: Number(fuelData.fuel_qty) || 0, diesel: Number(fuelData.diesel) || 0 });
      toast.success("Fuel recorded");
      setFuelOpen(false); setFuelRow(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save fuel"); }
  };

  const [tripDieselOcr, setTripDieselOcr] = useState(false);
  const handleTripDieselFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setTripDieselOcr(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/challans/fuel-ocr`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") },
      });
      setF((p) => ({
        ...p,
        diesel: data.amount ? String(data.amount) : p.diesel,
        fuel_qty: isDalmia && data.fuel_qty ? String(data.fuel_qty) : p.fuel_qty,
      }));
      toast.success("Diesel receipt read — trip advance filled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the diesel receipt");
    } finally {
      setTripDieselOcr(false);
    }
  };

  const save = async () => {
    if (!f.vehicle_number) return toast.error("Enter or select a vehicle");
    const hub = f.hub_id || (selectedHub !== "all" ? selectedHub : hubs[0]?.id || "");
    try {
      if (mixed) {
        const valid = lines.filter((l) => l.delivery_number || l.quantity);
        if (!editing && valid.length < 2) return toast.error("Add at least 2 delivery lines for mixed loading");
        if (editing && valid.length < 1) return toast.error("Keep at least 1 delivery line");
        if (editing) {
          const gid = lines.find((l) => l.group_id)?.group_id || "";
          const newGid = valid.length >= 2 ? (gid || crypto.randomUUID()) : "";
          const removed = mixOriginalIds.filter((id) => !valid.some((l) => l.id === id));
          for (const id of removed) await api.delete(`/challans/${id}`);
          for (let idx = 0; idx < valid.length; idx++) {
            const l = valid[idx];
            const payload = {
              gr_no: f.gr_no, gr_date: f.gr_date, vehicle_number: f.vehicle_number, owner_name: f.owner_name,
              vehicle_type: f.vehicle_type, market_rate_enabled: showMarket, hub_id: hub,
              material_type: l.material_type, load_location: l.load_location, delivery_location: l.delivery_location,
              delivery_number: l.delivery_number, quantity: Number(l.quantity) || 0,
              company_rate: Number(l.company_rate) || 0, market_rate: showMarket ? (Number(l.market_rate) || 0) : 0,
              diesel: idx === 0 ? (Number(f.diesel) || 0) : 0, cash: 0, bank_transfer: idx === 0 ? (Number(f.bank_transfer) || 0) : 0, fastag: 0, fuel_qty: idx === 0 ? (Number(f.fuel_qty) || 0) : 0, unloading_amount: Number(l.unloading_amount) || 0,
              group_id: newGid,
            };
            if (l.id) await api.put(`/challans/${l.id}`, payload);
            else await api.post("/challans", payload);
          }
          toast.success(`Mixed load updated · ${valid.length} deliveries`);
        } else {
          await api.post("/challans/bulk", {
            gr_no: f.gr_no, gr_date: f.gr_date, vehicle_number: f.vehicle_number, owner_name: f.owner_name,
            vehicle_type: f.vehicle_type, market_rate_enabled: showMarket, hub_id: hub,
            lines: valid.map((l, idx) => ({
              material_type: l.material_type, load_location: l.load_location, delivery_location: l.delivery_location,
              delivery_number: l.delivery_number, quantity: Number(l.quantity) || 0,
              company_rate: Number(l.company_rate) || 0, market_rate: showMarket ? (Number(l.market_rate) || 0) : 0,
              diesel: idx === 0 ? (Number(f.diesel) || 0) : 0, cash: 0, bank_transfer: idx === 0 ? (Number(f.bank_transfer) || 0) : 0, fastag: 0, fuel_qty: idx === 0 ? (Number(f.fuel_qty) || 0) : 0, unloading_amount: Number(l.unloading_amount) || 0,
            })),
          });
          toast.success(`Mixed load created · ${valid.length} deliveries`);
        }
      } else {
        if (!f.delivery_location) return toast.error("Delivery Location required");
        const payload = {
          ...f, hub_id: hub, group_id: "", quantity: Number(f.quantity) || 0, company_rate: Number(f.company_rate) || 0,
          market_rate_enabled: showMarket, market_rate: showMarket ? (Number(f.market_rate) || 0) : 0,
          diesel: Number(f.diesel) || 0, cash: Number(f.cash) || 0, bank_transfer: Number(f.bank_transfer) || 0, fastag: Number(f.fastag) || 0, fuel_qty: Number(f.fuel_qty) || 0, unloading_amount: Number(f.unloading_amount) || 0,
        };
        if (editing) { await api.put(`/challans/${editing}`, payload); toast.success("Challan updated"); }
        else { await api.post("/challans", payload); toast.success("Challan created"); }
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save challan"); }
  };
  const [confirmDel, setConfirmDel] = useState(null);
  const del = async (id) => { const { data } = await api.delete(`/challans/${id}`); toast.success(`Deleted${data?.bill_deleted ? ` · bill ${data.bill_deleted} also removed` : data?.bill_unlinked ? ` · unlinked from bill ${data.bill_unlinked}` : ""}`); load(); };
  const openMix = (r) => { setMixTarget(r); setMixSelected([]); };
  const toggleMixPick = (id) => setMixSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const confirmMix = async () => {
    if (!mixSelected.length) return toast.error("Select at least one other row to mix");
    await api.post("/challans/mix-group", { ids: [mixTarget.id, ...mixSelected], action: "mix" });
    toast.success(`Mixed ${mixSelected.length + 1} deliveries into one load`);
    setMixTarget(null); setMixSelected([]); load();
  };
  const unmix = async (r) => {
    const ids = rows.filter((x) => x.group_id && x.group_id === r.group_id).map((x) => x.id);
    await api.post("/challans/mix-group", { ids, action: "unmix" });
    toast.success(`Split ${ids.length} deliveries back to separate loads`);
    load();
  };

  const onMixedToggle = (v) => {
    setMixed(v);
    if (v && editing) {
      setMixOriginalIds([editing]);
      setLines([{ id: editing, group_id: "", material_type: f.material_type || "", load_location: f.load_location || "", delivery_location: f.delivery_location || "", delivery_number: f.delivery_number || "", quantity: f.quantity, company_rate: f.company_rate, market_rate: f.market_rate, unloading_amount: f.unloading_amount }, emptyLine()]);
    }
  };

  const [importing, setImporting] = useState(false);
  const [bulkScanOpen, setBulkScanOpen] = useState(false);
  const [dieselDiffs, setDieselDiffs] = useState(null);
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!selectedHub) return toast.error("Select a hub first");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("hub", selectedHub);
      const { data } = await axios.post(`${API}/challans/import`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") },
      });
      toast.success(`Imported ${data.created} challan(s)${data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicate(s) skipped` : ""}${data.errors?.length ? ` · ${data.errors.length} row error(s)` : ""}`);
      if (data.duplicate_list?.length) toast.warning(`Duplicates skipped: ${data.duplicate_list.slice(0, 10).join(", ")}${data.duplicate_list.length > 10 ? ` +${data.duplicate_list.length - 10} more` : ""}`, { duration: 8000 });
      if (data.errors?.length) console.warn("Import issues:", data.errors);
      if (data.diesel_diffs?.length) setDieselDiffs(data.diesel_diffs);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const exportCsv = () => {
    const header = ["Material", ...(isDalmia ? [] : ["GR No"]), "GR Date", "Load Location", "Delivery Location", "Vehicle", "Owner", "Vehicle Type", "Delivery No", "Quantity", "Company Rate", ...(isDalmia ? [] : ["Market Rate"]), "Diesel", "Bank Transfer", ...(isDalmia ? ["Fuel Qty (L)", "Unloading"] : [])];
    const l = locRows.map((r) => [r.material_type, ...(isDalmia ? [] : [r.gr_no]), r.gr_date, r.load_location, r.delivery_location, r.vehicle_number, r.owner_name, r.vehicle_type, r.delivery_number, r.quantity, r.company_rate, ...(isDalmia ? [] : [r.market_rate]), r.diesel, r.bank_transfer, ...(isDalmia ? [r.fuel_qty || 0, r.unloading_amount || 0] : [])].join(","));
    const blob = new Blob([[header.join(","), ...l].join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "dispatch.csv"; a.click();
  };

  const quickRange = (days) => {
    const now = new Date();
    const f2 = new Date(); f2.setDate(now.getDate() - days + 1);
    setFrom(ymd(f2)); setTo(ymd(now));
  };
  const clearFilters = () => { setQ(""); setFrom(""); setTo(""); setMaterial("all"); setVtype("all"); setLoc("all"); setDloc("all"); };
  const hasFilters = q || from || to || material !== "all" || vtype !== "all" || loc !== "all" || dloc !== "all";

  const mixTotal = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.company_rate) || 0), 0);

  return (
    <div>
      <h1 className="font-display text-2xl font-bold mb-6">Dispatch</h1>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9 w-64" placeholder="Search owner / vehicle / delivery / GR" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dispatch-search" />
        </div>
        <DatePicker value={from} onChange={setFrom} placeholder="From date" testid="dispatch-from-date" />
        <DatePicker value={to} onChange={setTo} placeholder="To date" testid="dispatch-to-date" />
        <Select value={material} onValueChange={setMaterial}>
          <SelectTrigger className="w-44" data-testid="dispatch-material-filter"><SelectValue placeholder="Material Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Materials</SelectItem>{materials.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={vtype} onValueChange={setVtype}>
          <SelectTrigger className="w-40" data-testid="dispatch-vtype-filter"><SelectValue placeholder="Truck Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem>{VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={loc} onValueChange={setLoc}>
          <SelectTrigger className="w-48" data-testid="dispatch-load-location-filter"><SelectValue placeholder="Load Location" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Load Locations</SelectItem>{locOptions.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={dloc} onValueChange={setDloc}>
          <SelectTrigger className="w-48" data-testid="dispatch-delivery-location-filter"><SelectValue placeholder="Delivery Location" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Delivery Locations</SelectItem>{dlocOptions.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <IconBtn onClick={load} data-testid="dispatch-refresh"><RotateCw className="h-4 w-4" /></IconBtn>
      </div>

      {/* Quick filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[{ l: "Today", d: 1 }, { l: "Last 7 days", d: 7 }, { l: "Last 30 days", d: 30 }].map((c) => (
          <button key={c.l} onClick={() => quickRange(c.d)} data-testid={`dispatch-chip-${c.d}`}
            className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors">{c.l}</button>
        ))}
        {hasFilters && (
          <button onClick={clearFilters} data-testid="dispatch-chip-clear"
            className="text-xs px-3 py-1.5 rounded-full border border-rose-500/40 text-rose-500 hover:bg-rose-500/10 transition-colors inline-flex items-center gap-1"><X className="h-3 w-3" />Clear</button>
        )}
        <span className="ml-auto text-xs text-muted-foreground" data-testid="dispatch-count">{locRows.length} challans</span>
      </div>

      <div className="flex items-center justify-end gap-2 mb-3">
        <label className="inline-flex">
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImport} disabled={importing} data-testid="dispatch-import-file" />
          <span className="inline-flex items-center gap-1.5 text-sm h-9 px-3 rounded-lg border border-border cursor-pointer hover:bg-muted/40 transition-colors" data-testid="dispatch-import-button"><Upload className="h-4 w-4" />{importing ? "Importing…" : "Import Excel"}</span>
        </label>
        <Button variant="outline" onClick={() => { if (!selectedHub || selectedHub === "all") return toast.error("Select a specific hub first"); setBulkScanOpen(true); }} data-testid="bulk-scan-button"><ScanLine className="h-4 w-4 mr-1" />Bulk Scan</Button>
        <IconBtn onClick={exportCsv} data-testid="dispatch-export"><FileSpreadsheet className="h-4 w-4" /></IconBtn>
        <IconBtn onClick={() => window.print()} data-testid="dispatch-print"><Printer className="h-4 w-4" /></IconBtn>
        <Button onClick={openCreate} data-testid="create-challan-button"><Plus className="h-4 w-4 mr-1" /> Create Challan</Button>
      </div>
      <div className="flex justify-end mb-2"><PageSize value={pageSize} onChange={setPageSize} /></div>

      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="dispatch-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr>
              <th className="py-3 px-4 text-left">Sl No</th>
              <th className="py-3 px-4 text-left">Material Type</th>
              {!isDalmia && <th className="py-3 px-4 text-left">GR No</th>}
              <th className="py-3 px-4 text-left">GR Date</th>
              <th className="py-3 px-4 text-left">Load Location</th>
              <th className="py-3 px-4 text-left">Delivery Location</th>
              <th className="py-3 px-4 text-left">Vehicle Number</th>
<SortHeader label="Owner Name" dir={osDir} onClick={osToggle} />
              <th className="py-3 px-4 text-left">Vehicle Type</th>
              <th className="py-3 px-4 text-left">Delivery / Shipment No</th>
              <th className="py-3 px-4 text-right">Quantity</th>
              <th className="py-3 px-4 text-right">Company Rate</th>
              {!isDalmia && <th className="py-3 px-4 text-right">Market Rate</th>}
              <th className="py-3 px-4 text-right">Diesel</th>
              <th className="py-3 px-4 text-right">Bank Transfer</th>
              {isDalmia && <><th className="py-3 px-4 text-right">Fuel Qty (L)</th><th className="py-3 px-4 text-right">Unloading ₹</th></>}
              <th className="py-3 px-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={r.id} className={`border-t border-border ${rowTone(mixIdx[r.group_id], serialByRow[r.id])} ${r.group_id && firstOfUnit[r.id] ? "row-mix-start" : ""} ${r.group_id && lastOfUnit[r.id] ? "border-b-2" : ""}`} data-testid={`dispatch-row-${r.id}`}>
                <td className="py-3 px-4 text-muted-foreground">{firstOfUnit[r.id] ? <span className="font-semibold text-foreground/80">{serialByRow[r.id]}</span> : <span className="pl-2 opacity-60" title="Part of the mixed load above">↳</span>}</td>
                <td className="py-3 px-4 font-medium uppercase">{r.material_type}{r.group_id && firstOfUnit[r.id] && unitCount[unitKeyByRow[r.id]] > 1 ? <MixChip count={unitCount[unitKeyByRow[r.id]]} mixIdx={mixIdx[r.group_id]} /> : null}</td>
                {!isDalmia && <td className="py-3 px-4 font-mono text-xs">{r.gr_no || "-"}</td>}
                <td className="py-3 px-4">{dt(r.gr_date)}</td>
                <td className="py-3 px-4">{r.load_location}</td>
                <td className="py-3 px-4 max-w-[180px] truncate">{r.delivery_location}</td>
                <td className="py-3 px-4 font-mono">{r.vehicle_number}</td>
                <td className="py-3 px-4"><OwnerBadge name={r.owner_name} /></td>
                <td className="py-3 px-4">{r.vehicle_type}</td>
                <td className="py-3 px-4 font-mono text-xs">{r.delivery_number}</td>
                <td className="py-3 px-4 text-right font-mono">{fmtNumber(r.quantity)}</td>
                <td className="py-3 px-4 text-right font-mono">{fmtNumber(r.company_rate)}</td>
                {!isDalmia && <td className="py-3 px-4 text-right font-mono">{fmtNumber(r.market_rate)}</td>}
                <td className="py-3 px-4 text-right font-mono">{firstOfUnit[r.id] ? fmtNumber(unitAgg[unitKeyByRow[r.id]].diesel) : <span className="text-muted-foreground/40">·</span>}</td>
                <td className="py-3 px-4 text-right font-mono">{firstOfUnit[r.id] ? fmtNumber(unitAgg[unitKeyByRow[r.id]].bank_transfer) : <span className="text-muted-foreground/40">·</span>}</td>
                {isDalmia && <><td className="py-3 px-4 text-right font-mono">{firstOfUnit[r.id] ? fmtNumber(unitAgg[unitKeyByRow[r.id]].fuel_qty) : <span className="text-muted-foreground/40">·</span>}</td><td className="py-3 px-4 text-right font-mono">{fmtNumber(r.unloading_amount || 0)}</td></>}
                <td className="py-2 px-3">
                  <div className="flex items-center justify-center gap-1.5">
                    <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs gap-1.5 font-medium" onClick={() => openEdit(r)} data-testid={`edit-challan-${r.id}`} title={r.group_id ? "Edit mixed load" : "Edit challan"}><Pencil className="h-3.5 w-3.5" />Edit</Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" data-testid={`actions-challan-${r.id}`} title="More actions"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Challan Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {firstOfUnit[r.id] && (
                          <DropdownMenuItem onClick={() => openFuel(canonRowByUnit[unitKeyByRow[r.id]])} className="gap-2.5 cursor-pointer" data-testid={`fuel-challan-${r.id}`}>
                            <span className="h-6 w-6 rounded-md bg-amber-500/10 inline-flex items-center justify-center shrink-0"><Fuel className="h-3.5 w-3.5 text-amber-500" /></span>Record Fuel
                          </DropdownMenuItem>
                        )}
                        {r.group_id
                          ? (firstOfUnit[r.id] && (
                              <DropdownMenuItem onClick={() => unmix(r)} className="gap-2.5 cursor-pointer" data-testid={`unmix-challan-${r.id}`}>
                                <span className="h-6 w-6 rounded-md bg-cyan-500/10 inline-flex items-center justify-center shrink-0"><Link2Off className="h-3.5 w-3.5 text-cyan-500" /></span>Un-mix Load ({unitCount[unitKeyByRow[r.id]]} deliveries)
                              </DropdownMenuItem>
                            ))
                          : (
                              <DropdownMenuItem onClick={() => openMix(r)} className="gap-2.5 cursor-pointer" data-testid={`mix-challan-${r.id}`}>
                                <span className="h-6 w-6 rounded-md bg-cyan-500/10 inline-flex items-center justify-center shrink-0"><Link2 className="h-3.5 w-3.5 text-cyan-500" /></span>Mix Load
                              </DropdownMenuItem>
                            )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setConfirmDel(r)} className="gap-2.5 cursor-pointer text-rose-500 focus:text-rose-500 focus:bg-rose-500/10" data-testid={`delete-challan-${r.id}`}>
                          <span className="h-6 w-6 rounded-md bg-rose-500/10 inline-flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></span>{r.group_id ? "Delete This Delivery" : "Delete Challan"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={16} className="py-16 text-center text-muted-foreground">No challans found</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => { if (!v) setConfirmDel(null); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDel?.group_id ? "Delete this delivery?" : "Delete this challan?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel && <>Truck <b className="font-mono">{confirmDel.vehicle_number}</b> · Delivery <b className="font-mono">{confirmDel.delivery_number || "-"}</b> · {fmtNumber(confirmDel.quantity)} T. </>}
              This permanently removes it{confirmDel?.bill_no ? " and unlinks it from its bill" : ""}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-600/90 text-white" data-testid="delete-confirm-yes" onClick={() => { const id = confirmDel.id; setConfirmDel(null); del(id); }}>Yes, delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!mixTarget} onOpenChange={(v) => { if (!v) { setMixTarget(null); setMixSelected([]); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="mix-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="h-4 w-4 text-cyan-500" />Mix load — combine with other deliveries</DialogTitle></DialogHeader>
          {mixTarget && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Base row: <span className="font-mono font-medium text-foreground">{mixTarget.delivery_number || "-"}</span> · {mixTarget.vehicle_number} · {dt(mixTarget.gr_date)} · {mixTarget.delivery_location}
              </div>
              <div className="space-y-1.5">
                {rows.filter((x) => x.id !== mixTarget.id).map((x) => {
                  const checked = mixSelected.includes(x.id);
                  return (
                    <button key={x.id} type="button" onClick={() => toggleMixPick(x.id)} data-testid={`mix-pick-${x.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left text-sm transition-colors ${checked ? "border-primary/60 bg-primary/10" : "border-border hover:bg-accent/50"}`}>
                      <span className={`h-4 w-4 rounded border inline-flex items-center justify-center shrink-0 text-[10px] font-bold ${checked ? "bg-primary border-primary text-white" : "border-muted-foreground/40 text-transparent"}`}>✓</span>
                      <span className="font-mono text-xs w-28 truncate">{x.delivery_number || "-"}</span>
                      <span className="font-mono">{x.vehicle_number}</span>
                      <span className="text-muted-foreground">{dt(x.gr_date)}</span>
                      <span className="text-muted-foreground truncate">{x.delivery_location}</span>
                      {x.group_id ? <span className="text-[10px] px-1 py-0.5 rounded bg-cyan-500/15 text-cyan-500 shrink-0">MIX</span> : null}
                      <span className="ml-auto font-mono shrink-0">{fmtNumber(x.quantity)} T</span>
                    </button>
                  );
                })}
                {rows.filter((x) => x.id !== mixTarget.id).length === 0 && (
                  <div className="py-8 text-center text-sm text-muted-foreground">No other rows available to mix</div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMixTarget(null); setMixSelected([]); }} data-testid="mix-cancel">Cancel</Button>
            <Button onClick={confirmMix} data-testid="mix-confirm"><Link2 className="h-4 w-4 mr-1" />Combine{mixSelected.length ? ` ${mixSelected.length + 1} rows` : ""}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="challan-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Challan" : "Create Challan"}</DialogTitle></DialogHeader>

          {(
            <div className="flex flex-wrap items-center gap-6 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
              {!isDalmia && (
                <>
                  <div className="flex items-center gap-2.5">
                    <Switch checked={marketEnabled} onCheckedChange={setMarketEnabled} data-testid="market-rate-toggle" />
                    <span className="text-sm font-medium">Market Rate</span>
                    <span className="text-xs text-muted-foreground">{marketEnabled ? "On" : "Off"}</span>
                  </div>
                  <div className="h-6 w-px bg-border hidden sm:block" />
                </>
              )}
              <div className="flex items-center gap-2.5">
                <Switch checked={mixed} onCheckedChange={onMixedToggle} data-testid="mixed-loading-toggle" />
                <Layers className="h-4 w-4 text-cyan-500" />
                <span className="text-sm font-medium">Mixed Loading</span>
                <span className="text-xs text-muted-foreground">{mixed ? "Multiple deliveries on one truck" : "Single delivery"}</span>
              </div>
            </div>
          )}

          {!editing && !mixed && (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3" data-testid="challan-invoice-ocr-box">
              <ScanLine className="h-5 w-5 text-primary" />
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-medium">Scan Invoice / Challan</div>
                <div className="text-xs text-muted-foreground">Auto-fill Date, Shipment No, Vehicle, Material, Load & Delivery Location & Quantity</div>
              </div>
              <label className="shrink-0">
                <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleInvoiceFile} disabled={invOcr} data-testid="challan-invoice-ocr-file" />
                <span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground cursor-pointer hover:opacity-90" data-testid="challan-invoice-ocr-button">
                  <ScanLine className="h-4 w-4" />{invOcr ? "Reading…" : "Scan Invoice"}
                </span>
              </label>
            </div>
          )}

          {/* Shared header */}
          <Section title="Trip Details">
            <div className="grid grid-cols-3 gap-4">
            <Field label="GR Date">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start font-normal" data-testid="challan-grdate-trigger">
                    <CalendarIcon className="mr-2 h-4 w-4 opacity-70" />{f.gr_date ? dt(f.gr_date) : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={f.gr_date ? new Date(f.gr_date) : undefined} onSelect={(d) => setF({ ...f, gr_date: toISO(d) })} initialFocus />
                </PopoverContent>
              </Popover>
            </Field>
            {!isDalmia && <Field label="GR No"><Input data-testid="challan-grno-input" value={f.gr_no} onChange={(e) => setF({ ...f, gr_no: e.target.value })} placeholder="Auto" /></Field>}
            <Field label="Vehicle Number">
              <Combobox value={f.vehicle_number} onChange={onVehicleChange} onPick={onVehicleChange} options={vehicleNumbers} placeholder="Type or select vehicle" testid="challan-vehicle-input" />
              {f.vehicle_number && !inFleet(f.vehicle_number) && <p className="text-xs text-amber-600 mt-1" data-testid="vehicle-fleet-warning">New truck — it will be added to Truck Master automatically on save</p>}
            </Field>
            <Field label="Owner Name"><Input data-testid="challan-owner-input" value={f.owner_name} onChange={(e) => setF({ ...f, owner_name: e.target.value })} placeholder="Auto from vehicle" /></Field>
            <Field label="Vehicle Type">
              <Select value={f.vehicle_type} onValueChange={(v) => setF({ ...f, vehicle_type: v })}>
                <SelectTrigger data-testid="challan-vtype-select"><SelectValue /></SelectTrigger>
                <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div />
            </div>
          </Section>

          {/* Single mode */}
          {!mixed && (
            <div className="space-y-4">
              <Section title="Route, Load & Rates">
                <div className="grid grid-cols-3 gap-4">
              <Field label="Material Type">
                <Combobox value={f.material_type} onChange={(v) => setF({ ...f, material_type: v })} options={materials} placeholder="Type or select" testid="challan-material-input" />
              </Field>
              <Field label="Load Location">
                <Combobox value={f.load_location} onChange={(v) => setF({ ...f, load_location: v })} options={loadLocs} placeholder="Type or select" testid="challan-load-input" />
              </Field>
              <Field label="Delivery Location">
                <Combobox value={f.delivery_location} onChange={(v) => setF({ ...f, delivery_location: v })} options={delLocs} placeholder="Type or select" testid="challan-delivery-input" />
              </Field>
              <Field label="Delivery Number / Shipment No"><Input data-testid="challan-deliverynum-input" value={f.delivery_number} onChange={(e) => setF({ ...f, delivery_number: e.target.value })} /></Field>
              <Field label="Quantity (Tons)"><Input type="number" data-testid="challan-qty-input" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} /></Field>
              <Field label="Company Rate (₹/Ton)"><Input type="number" data-testid="challan-companyrate-input" value={f.company_rate} onChange={(e) => setF({ ...f, company_rate: e.target.value })} /></Field>
              {showMarket && (
                <Field label="Market Rate (₹/Ton)"><Input type="number" data-testid="challan-marketrate-input" value={f.market_rate} onChange={(e) => setF({ ...f, market_rate: e.target.value })} /></Field>
              )}
                </div>
              </Section>
              <Section title="Advances (₹)">
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3 mb-4" data-testid="single-diesel-ocr-box">
                  <Fuel className="h-5 w-5 text-amber-500" />
                  <div className="flex-1 min-w-[160px]">
                    <div className="text-sm font-medium">Scan Diesel Receipt</div>
                    <div className="text-xs text-muted-foreground">Fills Diesel ₹{isDalmia ? " & Fuel Qty" : ""} from the receipt</div>
                  </div>
                  <label className="shrink-0">
                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleTripDieselFile} disabled={tripDieselOcr} data-testid="single-diesel-ocr-file" />
                    <span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground cursor-pointer hover:opacity-90" data-testid="single-diesel-ocr-button">
                      <ScanLine className="h-4 w-4" />{tripDieselOcr ? "Reading…" : "Scan Diesel"}
                    </span>
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-4">
              <Field label="Diesel (Advance ₹)"><Input type="number" data-testid="challan-diesel-input" value={f.diesel} onChange={(e) => setF({ ...f, diesel: e.target.value })} /></Field>
              <Field label="Bank Transfer (Advance ₹)"><Input type="number" data-testid="challan-bank-input" value={f.bank_transfer} onChange={(e) => setF({ ...f, bank_transfer: e.target.value })} /></Field>
              {isDalmia && <Field label="Fuel Qty (Litres)"><Input type="number" data-testid="challan-fuelqty-input" value={f.fuel_qty} onChange={(e) => setF({ ...f, fuel_qty: e.target.value })} /></Field>}
              {isDalmia && <Field label="Unloading Amount (₹)"><Input type="number" data-testid="challan-unloading-input" value={f.unloading_amount} onChange={(e) => setF({ ...f, unloading_amount: e.target.value })} /></Field>}
                </div>
              </Section>
            </div>
          )}

          {/* Mixed mode */}
          {mixed && (
            <div className="space-y-3">
              {!editing && (
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3" data-testid="challan-mixed-invoice-ocr-box">
                  <ScanLine className="h-5 w-5 text-primary" />
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-medium">Scan Multiple Invoices</div>
                    <div className="text-xs text-muted-foreground">Each invoice becomes a delivery line (shipment no, material, location & qty)</div>
                  </div>
                  <label className="shrink-0">
                    <input type="file" accept="application/pdf,image/*" multiple className="hidden" onChange={handleMixedInvoiceFiles} disabled={mixInvOcr} data-testid="challan-mixed-invoice-ocr-file" />
                    <span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground cursor-pointer hover:opacity-90" data-testid="challan-mixed-invoice-ocr-button">
                      <ScanLine className="h-4 w-4" />{mixInvOcr ? "Reading…" : "Scan Invoices"}
                    </span>
                  </label>
                </div>
              )}
              {lines.map((l, i) => (
                <div key={i} className="rounded-xl border border-border p-3 relative" data-testid={`mix-line-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-cyan-500">Delivery {i + 1}</span>
                    {lines.length > 1 && <button onClick={() => setLines((p) => p.filter((_, j) => j !== i))} className="text-rose-500" data-testid={`mix-remove-${i}`}><X className="h-4 w-4" /></button>}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Combobox value={l.material_type} onChange={(v) => setLine(i, "material_type", v)} options={materials} placeholder="Material" testid={`mix-material-${i}`} />
                    <Combobox value={l.load_location} onChange={(v) => setLine(i, "load_location", v)} options={loadLocs} placeholder="Load Loc" testid={`mix-load-${i}`} />
                    <Combobox value={l.delivery_location} onChange={(v) => setLine(i, "delivery_location", v)} options={delLocs} placeholder="Delivery Loc" testid={`mix-delivery-${i}`} />
                    <Input placeholder="Delivery / Shipment No" value={l.delivery_number} onChange={(e) => setLine(i, "delivery_number", e.target.value)} data-testid={`mix-dn-${i}`} />
                    <Input type="number" placeholder="Qty (Tons)" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`mix-qty-${i}`} />
                    <Input type="number" placeholder="Company Rate" value={l.company_rate} onChange={(e) => setLine(i, "company_rate", e.target.value)} data-testid={`mix-crate-${i}`} />
                    {showMarket && <Input type="number" placeholder="Market Rate" value={l.market_rate} onChange={(e) => setLine(i, "market_rate", e.target.value)} data-testid={`mix-mrate-${i}`} />}
                    {isDalmia && <Input type="number" placeholder="Unloading ₹" value={l.unloading_amount} onChange={(e) => setLine(i, "unloading_amount", e.target.value)} data-testid={`mix-unloading-${i}`} />}
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setLines((p) => [...p, emptyLine()])} data-testid="mix-add-line"><Plus className="h-4 w-4 mr-1" />Add Delivery</Button>
              <Section title="Trip Advances (whole trip, not per delivery)">
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3 mb-4" data-testid="mix-trip-diesel-ocr-box">
                  <Fuel className="h-5 w-5 text-amber-500" />
                  <div className="flex-1 min-w-[160px]">
                    <div className="text-sm font-medium">Scan Diesel Receipt</div>
                    <div className="text-xs text-muted-foreground">One receipt for the whole trip{isDalmia ? " — fills Diesel ₹ & Fuel Qty" : " — fills Diesel ₹"}</div>
                  </div>
                  <label className="shrink-0">
                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleTripDieselFile} disabled={tripDieselOcr} data-testid="mix-trip-diesel-ocr-file" />
                    <span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground cursor-pointer hover:opacity-90" data-testid="mix-trip-diesel-ocr-button">
                      <ScanLine className="h-4 w-4" />{tripDieselOcr ? "Reading…" : "Scan Diesel"}
                    </span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Diesel (Advance ₹)"><Input type="number" data-testid="mix-trip-diesel" value={f.diesel} onChange={(e) => setF({ ...f, diesel: e.target.value })} /></Field>
                  <Field label="Bank Transfer (Advance ₹)"><Input type="number" data-testid="mix-trip-bank" value={f.bank_transfer} onChange={(e) => setF({ ...f, bank_transfer: e.target.value })} /></Field>
                  {isDalmia && <Field label="Fuel Qty (Litres)"><Input type="number" data-testid="mix-trip-fuelqty" value={f.fuel_qty} onChange={(e) => setF({ ...f, fuel_qty: e.target.value })} /></Field>}
                </div>
              </Section>
            </div>
          )}

          <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 px-6 py-4 bg-card/95 backdrop-blur-md border-t border-border/60 flex items-center justify-between gap-4">
            <div className="text-sm">
              {mixed ? (
                <span className="text-muted-foreground">Total Freight: <span className="font-mono font-semibold text-foreground">{fmtCurrency(mixTotal)}</span> · {lines.length} deliveries</span>
              ) : (
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-muted-foreground">Freight: <span className="font-mono font-semibold text-foreground">{fmtCurrency((Number(f.quantity) || 0) * (Number(f.company_rate) || 0))}</span></span>
                  {showMarket && <span className="text-muted-foreground">Commission: <span className="font-mono font-semibold text-emerald-500">{fmtCurrency((Number(f.quantity) || 0) * ((Number(f.company_rate) || 0) - (Number(f.market_rate) || 0)))}</span></span>}
                </div>
              )}
            </div>
            <Button onClick={save} data-testid="save-challan-button">{editing ? (mixed ? "Update Mixed Load" : "Update Challan") : mixed ? "Create Mixed Load" : "Create Challan"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={fuelOpen} onOpenChange={setFuelOpen}>
        <DialogContent className="max-w-md" data-testid="fuel-dialog">
          <DialogHeader><DialogTitle>Record Fuel</DialogTitle></DialogHeader>
          {fuelRow && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground">
                <span className="font-mono font-medium text-foreground">{fuelRow.vehicle_number}</span> · {fuelRow.delivery_number || "-"} · {dt(fuelRow.gr_date)}
              </div>
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3" data-testid="fuel-ocr-box">
                <Fuel className="h-5 w-5 text-amber-500" />
                <div className="flex-1 min-w-[140px]">
                  <div className="text-sm font-medium">Scan Fuel Receipt</div>
                  <div className="text-xs text-muted-foreground">Auto-fill Fuel Qty (L) & Amount (₹)</div>
                </div>
                <label className="shrink-0">
                  <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFuelFile} disabled={fuelOcr} data-testid="fuel-ocr-file" />
                  <span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground cursor-pointer hover:opacity-90" data-testid="fuel-ocr-button">
                    <ScanLine className="h-4 w-4" />{fuelOcr ? "Reading…" : "Scan"}
                  </span>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Fuel Qty (Litres)"><Input type="number" data-testid="fuel-qty-input" value={fuelData.fuel_qty} onChange={(e) => setFuelData({ ...fuelData, fuel_qty: e.target.value })} /></Field>
                <Field label="Diesel Amount (₹)"><Input type="number" data-testid="fuel-amount-input" value={fuelData.diesel} onChange={(e) => setFuelData({ ...fuelData, diesel: e.target.value })} /></Field>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveFuel} data-testid="fuel-save-button">Save Fuel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {bulkScanOpen && <BulkScan hub={selectedHub} vehicles={vehicles} materials={materials} loadLocs={loadLocs} delLocs={delLocs} onClose={() => setBulkScanOpen(false)} onDone={() => { setBulkScanOpen(false); load(); }} />}
      {dieselDiffs && <DieselDiffDialog hubId={selectedHub} diffs={dieselDiffs} onClose={() => setDieselDiffs(null)} onDone={() => { setDieselDiffs(null); load(); }} />}
    </div>
  );
}

function BulkScan({ hub, vehicles, materials, loadLocs, delLocs, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [results, setResults] = useState(null);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") });
  const fleetSet = new Set(vehicles.map((v) => (v.vehicle_number || "").trim().toUpperCase()));
  const vehicleNumbers = vehicles.map((v) => v.vehicle_number);
  const isFleet = (vn) => fleetSet.has((vn || "").trim().toUpperCase());
  const scan = async () => {
    if (!files.length) return toast.error("Choose invoice files first");
    setScanning(true);
    try {
      const fd = new FormData(); files.forEach((f) => fd.append("files", f));
      const { data } = await axios.post(`${API}/challans/scan-preview`, fd, { headers: authHeaders() });
      setRows((data.items || []).map((it) => ({ ...it, skip: !!it.error })));
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };
  const setRow = (i, patch) => setRows((p) => p.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const onVeh = (i, vn) => {
    const v = vehicles.find((x) => (x.vehicle_number || "").toLowerCase() === (vn || "").trim().toLowerCase());
    setRow(i, { vehicle_number: vn, owner_name: v ? v.owner_name : "", vehicle_type: v ? v.vehicle_type : "Open" });
  };
  const active = (rows || []).filter((r) => !r.skip);
  const blocked = [];
  const ready = active;
  const create = async () => {
    if (blocked.length) return toast.error(`${blocked.length} row(s) have a truck not in Truck Master — fix or skip them`);
    if (!ready.length) return toast.error("No rows to create");
    setCreating(true);
    try {
      const payload = { hub_id: hub, rows: ready.map((r) => ({ gr_date: r.gr_date, delivery_number: r.delivery_number, vehicle_number: r.vehicle_number, owner_name: r.owner_name, vehicle_type: r.vehicle_type, material_type: r.material_type, load_location: r.load_location, delivery_location: r.delivery_location, quantity: Number(r.quantity) || 0, company_rate: 0 })) };
      const { data } = await axios.post(`${API}/challans/scan-create`, payload, { headers: { ...authHeaders(), "Content-Type": "application/json" } });
      setResults(data); toast.success(`Created ${data.created} challan(s)${data.failed ? `, ${data.failed} skipped` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-scan-dialog">
        <DialogHeader><DialogTitle>Bulk Scan Challans</DialogTitle></DialogHeader>
        {!rows && !results && (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-5 text-center">
            <ScanLine className="h-6 w-6 text-primary mx-auto mb-2" />
            <label className="inline-block">
              <input type="file" accept="application/pdf,image/*" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="bulk-scan-file" />
              <span className="text-sm px-3 py-2 rounded-lg border border-border cursor-pointer hover:bg-accent">Choose invoice files</span>
            </label>
            <p className="text-xs text-muted-foreground mt-2">{files.length ? `${files.length} file(s) selected` : "Each file becomes one dispatch challan — you review before creating"}</p>
          </div>
        )}
        {rows && !results && (
          <div className="space-y-3" data-testid="bulk-scan-review">
            <div className="text-xs text-muted-foreground">Review each challan. Rows whose truck is not in Truck Master are flagged amber — new trucks are added to Truck Master automatically when created.</div>
            {rows.map((r, i) => (
              <div key={i} className={`rounded-xl border p-3 ${r.skip ? "opacity-50" : (!isFleet(r.vehicle_number) ? "border-amber-500/50" : "border-border")}`} data-testid={`bulk-scan-row-${i}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-xs font-mono truncate">{r.filename}</span>
                  <label className="text-xs flex items-center gap-1.5 shrink-0"><input type="checkbox" checked={r.skip} onChange={(e) => setRow(i, { skip: e.target.checked })} data-testid={`bulk-scan-skip-${i}`} />Skip</label>
                </div>
                {r.error ? <div className="text-xs text-rose-500">{r.error}</div> : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="col-span-2 md:col-span-1">
                      <Combobox value={r.vehicle_number} onChange={(v) => onVeh(i, v)} options={vehicleNumbers} placeholder="Vehicle" testid={`bulk-scan-veh-${i}`} />
                      {!isFleet(r.vehicle_number) && <span className="text-[11px] text-amber-600" data-testid={`bulk-scan-flag-${i}`}>New truck — will be added to Truck Master</span>}
                    </div>
                    <Input type="date" value={r.gr_date || ""} onChange={(e) => setRow(i, { gr_date: e.target.value })} data-testid={`bulk-scan-date-${i}`} />
                    <Input placeholder="Delivery / Shipment No" value={r.delivery_number || ""} onChange={(e) => setRow(i, { delivery_number: e.target.value })} data-testid={`bulk-scan-dn-${i}`} />
                    <Input type="number" placeholder="Qty (Tons)" value={r.quantity || ""} onChange={(e) => setRow(i, { quantity: e.target.value })} data-testid={`bulk-scan-qty-${i}`} />
                    <Combobox value={r.material_type} onChange={(v) => setRow(i, { material_type: v })} options={materials} placeholder="Material" testid={`bulk-scan-mat-${i}`} />
                    <Combobox value={r.load_location} onChange={(v) => setRow(i, { load_location: v })} options={loadLocs} placeholder="Load Loc" testid={`bulk-scan-load-${i}`} />
                    <Combobox value={r.delivery_location} onChange={(v) => setRow(i, { delivery_location: v })} options={delLocs} placeholder="Delivery Loc" testid={`bulk-scan-del-${i}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {results && (
          <div className="max-h-72 overflow-y-auto border border-border rounded-lg divide-y divide-border" data-testid="bulk-scan-results">
            {results.results.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="font-mono text-xs truncate">{r.delivery_number || "(no DN)"}</span>
                {r.created ? <span className="text-emerald-500 text-xs">✓ Created</span> : <span className="text-rose-500 text-xs">{r.error}</span>}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          {!rows && !results && <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={scan} disabled={scanning || !files.length} data-testid="bulk-scan-run">{scanning ? "Scanning…" : `Scan ${files.length || ""}`}</Button></>}
          {rows && !results && <><Button variant="outline" onClick={() => { setRows(null); setFiles([]); }} data-testid="bulk-scan-back">Back</Button><Button onClick={create} disabled={creating || blocked.length > 0 || ready.length === 0} data-testid="bulk-scan-create">{creating ? "Creating…" : (blocked.length ? `${blocked.length} truck(s) not in fleet` : `Create ${ready.length} Challan${ready.length === 1 ? "" : "s"}`)}</Button></>}
          {results && <Button onClick={onDone} data-testid="bulk-scan-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
